import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Stethoscope, HeartPulse, CalendarClock, Pill, FileHeart, Activity,
  ShieldCheck, MessageSquare, Bot, Check, ArrowRight,
} from "lucide-react";

const FEATURES = [
  { icon: Bot, title: "Voice-to-SOAP in seconds", body: "Dictate like you would to a colleague. AI structures a clean SOAP note you edit and sign." },
  { icon: MessageSquare, title: "WhatsApp-native comms", body: "Confirmations, reminders, intake forms and recalls flow through the channel 91% of SA patients use." },
  { icon: ShieldCheck, title: "POPIA-native by design", body: "Every record has an audit trail, consent capture and role-based masking — export a CSV if an audit lands." },
  { icon: CalendarClock, title: "Zero-touch scheduling", body: "Patients reply on WhatsApp; reminders, reschedules and waitlist fills happen without front-desk clicks." },
  { icon: FileHeart, title: "Auto medical-aid claims", body: "Invoice created → formatted for Discovery, Bonitas, GEMS & Momentum → submitted → reconciled." },
  { icon: Activity, title: "Live practice KPIs", body: "No-show rate, patient LTV, top procedures by revenue and claim-rejection rate — always current." },
];

const VERTICALS = [
  { icon: Stethoscope, label: "Dental" },
  { icon: HeartPulse, label: "Physiotherapy" },
  { icon: Activity, label: "Chiropractic" },
  { icon: Pill, label: "GP / Family" },
  { icon: FileHeart, label: "Optometry" },
];

const PLANS = [
  { name: "Solo", price: "R150", per: "/ patient / mo", tagline: "Single-practitioner practices", features: ["1 practitioner", "Bookings + SOAP notes", "WhatsApp reminders", "POPIA audit trail"], popular: false },
  { name: "Pro", price: "R250", per: "/ patient / mo", tagline: "Growing multi-partner clinics", features: ["Up to 5 practitioners", "AI SOAP + letters", "Medical-aid claims", "Recall & waitlist automation", "KPI dashboard"], popular: true },
  { name: "Custom", price: "Let's talk", per: "", tagline: "Groups & franchises", features: ["Unlimited practitioners", "Dedicated onboarding", "Custom n8n workflows", "SLA & priority support"], popular: false },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Stethoscope className="h-5 w-5" />
            </div>
            <span className="text-lg font-bold tracking-tight">Practice OS™</span>
          </div>
          <nav className="hidden items-center gap-8 text-sm text-muted-foreground md:flex">
            <a href="#features" className="hover:text-foreground">Features</a>
            <a href="#verticals" className="hover:text-foreground">Practices</a>
            <a href="#pricing" className="hover:text-foreground">Pricing</a>
          </nav>
          <Link to="/login">
            <Button data-testid="header-login-btn" className="rounded-full">Sign in</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-radial-fade">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:py-28">
          <div className="animate-rise">
            <Badge variant="secondary" className="mb-5 gap-1.5 rounded-full px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Built for South African practices
            </Badge>
            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              One calm workspace for{" "}
              <span className="font-serif-accent italic text-primary">every practice.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base text-muted-foreground sm:text-lg">
              Front desk, chairside, billing and medical-aid claims — unified. AI drafts your notes,
              WhatsApp handles your patients, and nothing gets typed twice.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button size="lg" data-testid="hero-cta-btn" className="rounded-full gap-2">
                  Enter the demo <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button size="lg" variant="outline" className="rounded-full">See pricing</Button>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> POPIA-native · HPCSA-aware · Same-day setup
            </div>
          </div>

          {/* Dashboard mockup card */}
          <div className="animate-rise lg:justify-self-end" style={{ animationDelay: "0.15s" }}>
            <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-5 shadow-xl">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <CalendarClock className="h-4 w-4 text-primary" /> Today · 8 bookings
                </div>
                <Badge variant="secondary" className="rounded-full text-[10px]">Live</Badge>
              </div>
              <div className="space-y-2.5 py-4">
                {[
                  ["08:30", "Sipho Nkosi", "Scale & Polish", "checked-in"],
                  ["09:00", "Aisha Patel", "Consultation", "scheduled"],
                  ["09:30", "J. van der Merwe", "Filling", "scheduled"],
                ].map(([t, n, p, s]) => (
                  <div key={n} className="flex items-center gap-3 rounded-xl bg-secondary/60 px-3 py-2.5">
                    <div className="w-12 text-xs font-semibold text-primary">{t}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{n}</div>
                      <div className="text-[11px] text-muted-foreground">{p}</div>
                    </div>
                    <Badge variant="outline" className="rounded-full text-[10px] capitalize">{s}</Badge>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 border-t border-border/60 pt-4">
                {[["No-show", "4.2%"], ["MRR", "R250"], ["Claims", "92%"]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-secondary/50 p-2.5 text-center">
                    <div className="text-base font-bold text-foreground">{v}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verticals */}
      <section id="verticals" className="border-y border-border/50 bg-secondary/30">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-5 py-8">
          <span className="label-eyebrow">Supported day one</span>
          {VERTICALS.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Icon className="h-4 w-4 text-primary" /> {label}
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-5 py-20">
        <div className="max-w-2xl">
          <span className="label-eyebrow">Everything clever</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Why practices pay for calm, not clutter
          </h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="lift rounded-2xl border border-border/60 bg-card p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span className="label-eyebrow">Pricing</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Simple plans, priced in Rand
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PLANS.map((p) => (
              <div
                key={p.name}
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative rounded-2xl border bg-card p-7 ${
                  p.popular ? "border-primary/30 ring-2 ring-primary/25 shadow-lg" : "border-border/60"
                }`}
              >
                {p.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full">Most popular</Badge>
                )}
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tracking-tight">{p.price}</span>
                  <span className="text-sm text-muted-foreground">{p.per}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/login">
                  <Button className="mt-7 w-full rounded-full" variant={p.popular ? "default" : "outline"}>
                    Get started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border/50">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-8 text-sm text-muted-foreground sm:flex-row">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">Practice OS™</span>
          </div>
          <p>© 2026 Elysiom. POPIA-native healthcare workspace.</p>
        </div>
      </footer>
    </div>
  );
}
